import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { loadWeb3, getWeb3Instance, disconnectWallet } from "../Connectivity/Connectivity";

const WalletContext = createContext(null);

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be used inside <WalletProvider>");
  return ctx;
};

export function WalletProvider({ children }) {
  const [account, setAccount] = useState("");
  const [connecting, setConnecting] = useState(false);

  const shortAccount = account
    ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}`
    : "";

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const acc = await loadWeb3();
      if (acc === "No Wallet") {
        toast.error("No wallet detected. Please install MetaMask.");
      } else if (acc === "Wrong Network") {
        toast.error("Please switch to opBNB Mainnet");
      } else {
        setAccount(acc);
        toast.success("Connected to opBNB Mainnet");
      }
    } catch (error) {
      console.error("Connection error:", error);
      toast.error("Connection failed");
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    await disconnectWallet();
    setAccount("");
    toast.info("Wallet disconnected");
  }, []);

  // Reconnect silently when the page is opened again with an already-authorised wallet
  useEffect(() => {
    (async () => {
      if (!window.ethereum) return;
      try {
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        if (accounts && accounts.length > 0) {
          const chainId = await window.ethereum.request({ method: "eth_chainId" });
          if (parseInt(chainId, 16) === 204) {
            getWeb3Instance();
            setAccount(accounts[0]);
          }
        }
      } catch (error) {
        console.warn("Silent reconnect skipped:", error?.message);
      }
    })();
  }, []);

  // React to wallet / network changes
  useEffect(() => {
    if (!window.ethereum) return undefined;

    const onAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setAccount("");
        toast.info("Wallet disconnected");
      } else {
        setAccount(accounts[0]);
        toast.info("Account switched");
      }
    };

    const onChainChanged = (chainId) => {
      if (parseInt(chainId, 16) !== 204) {
        setAccount("");
        toast.error("Wrong network — please switch back to opBNB Mainnet");
      } else {
        window.location.reload();
      }
    };

    window.ethereum.on("accountsChanged", onAccountsChanged);
    window.ethereum.on("chainChanged", onChainChanged);

    return () => {
      window.ethereum.removeListener?.("accountsChanged", onAccountsChanged);
      window.ethereum.removeListener?.("chainChanged", onChainChanged);
    };
  }, []);

  return (
    <WalletContext.Provider
      value={{ account, shortAccount, connecting, connect, disconnect }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export default WalletContext;
